import { useCallback, useState } from "react";

/**
 * Width of an element, kept up to date with a ResizeObserver. Layouts that depend on the space they really
 * get (the sidebar takes a different share of the screen on a laptop, a tablet and a phone) switch on it
 * instead of on the viewport width.
 */
export function useElementWidth<T extends HTMLElement = HTMLDivElement>(): [
  (node: T | null) => void,
  number,
] {
  const [width, setWidth] = useState(0);
  const [observer] = useState(() =>
    typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver((entries) => {
          const entry = entries[entries.length - 1];
          if (entry) setWidth(Math.round(entry.contentRect.width));
        }),
  );

  const ref = useCallback(
    (node: T | null) => {
      observer?.disconnect();
      if (node) {
        setWidth(Math.round(node.getBoundingClientRect().width));
        observer?.observe(node);
      }
    },
    [observer],
  );

  return [ref, width];
}
